import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Users, Mail, RefreshCw, Send, GraduationCap, Loader2, Search, Bell } from "lucide-react";
import { BulkActionsBar, BulkCheckbox } from "@/components/admin/BulkActionsBar";

export default function AdminFormationDetail() {
  const { id } = useParams();
  const [formation, setFormation] = useState(null);
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Bulk selection
  const [sel, setSel] = useState(new Set());
  const toggleSel = (rid) => { const n = new Set(sel); n.has(rid) ? n.delete(rid) : n.add(rid); setSel(n); };
  const selectAll = () => setSel(new Set(regs.map((r) => r.id)));
  const clearSel = () => setSel(new Set());

  // Reminder modal
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderSubject, setReminderSubject] = useState("");
  const [reminderBody, setReminderBody] = useState("");
  const [sending, setSending] = useState(false);

  // Notify contacts modal
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactQuery, setContactQuery] = useState("");
  const [selContacts, setSelContacts] = useState(new Set());
  const [extraEmails, setExtraEmails] = useState("");
  const [notifyMsg, setNotifyMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, r] = await Promise.all([
        api.get(`/admin/formations`).then((res) => {
          const list = Array.isArray(res.data) ? res.data : (res.data?.items || []);
          return list.find((x) => x.id === id);
        }),
        api.get(`/admin/formations/${id}/registrations`),
      ]);
      setFormation(f);
      setRegs(r.data || []);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const resendStripe = async (regId) => {
    try {
      const r = await api.post(`/admin/formations/${id}/registrations/${regId}/resend-stripe`);
      toast.success(`Lien Stripe régénéré & envoyé`);
      window.open(r.data.checkout_url, "_blank");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur Stripe"); }
  };

  const markPaid = async (regId, paid) => {
    const note = paid ? (window.prompt("Note (réf. virement, n° chèque…) — optionnel :") || "") : "";
    try {
      await api.post(`/admin/formations/registrations/${regId}/mark-paid`, { paid, method: "transfer", note });
      toast.success(paid ? "Inscription marquée payée" : "Marquage retiré");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };

  const sendReminder = async (e) => {
    e?.preventDefault();
    if (sel.size === 0) return toast.error("Sélectionnez au moins 1 participant");
    setSending(true);
    try {
      const r = await api.post(`/admin/formations/${id}/registrations/bulk-reminder`, {
        ids: Array.from(sel),
        subject: reminderSubject,
        body: reminderBody,
      });
      toast.success(`Rappel envoyé à ${r.data.sent} participant${r.data.sent > 1 ? "s" : ""}`);
      setReminderOpen(false); clearSel();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
    finally { setSending(false); }
  };

  // Contacts loader for notify modal
  const loadContacts = async () => {
    setContactsLoading(true);
    try {
      const r = await api.get("/admin/contacts", { params: { per_page: 200 } });
      setContacts(r.data?.items || r.data || []);
    } catch { toast.error("Erreur chargement contacts"); }
    finally { setContactsLoading(false); }
  };

  const openNotify = () => { setNotifyOpen(true); if (contacts.length === 0) loadContacts(); };
  const toggleContact = (cid) => { const n = new Set(selContacts); n.has(cid) ? n.delete(cid) : n.add(cid); setSelContacts(n); };
  const filteredContacts = contactQuery
    ? contacts.filter((c) => `${c.first_name || ""} ${c.last_name || ""} ${c.email || ""} ${c.company_name || ""}`.toLowerCase().includes(contactQuery.toLowerCase()))
    : contacts;

  const sendNotify = async (e) => {
    e?.preventDefault();
    const emails = extraEmails.split(/[,\n;]+/).map((s) => s.trim()).filter(Boolean);
    if (selContacts.size === 0 && emails.length === 0) return toast.error("Sélectionnez au moins un contact ou ajoutez un email");
    setSending(true);
    try {
      const r = await api.post(`/admin/formations/${id}/notify-contacts`, {
        contact_ids: Array.from(selContacts),
        emails,
        message: notifyMsg,
      });
      toast.success(`Notification envoyée à ${r.data.sent} destinataire${r.data.sent > 1 ? "s" : ""}`);
      setNotifyOpen(false); setSelContacts(new Set()); setExtraEmails(""); setNotifyMsg("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
    finally { setSending(false); }
  };

  const paidCount = regs.filter((r) => r.payment_status === "paid").length;
  const pendingCount = regs.filter((r) => r.payment_status !== "paid").length;

  return (
    <PortalLayout sidebar="admin">
      <Link to="/admin/formations" className="inline-flex items-center gap-1.5 text-sm font-medium text-wine-600 hover:text-wine-700"><ArrowLeft className="h-4 w-4" /> Retour aux formations</Link>

      {loading ? (
        <div className="mt-12 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-wine-500" /></div>
      ) : !formation ? (
        <div className="mt-12 text-center text-neutral-500">Formation introuvable.</div>
      ) : (
        <>
          <div className="mt-5 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="flex items-center gap-2 text-3xl font-bold text-navy md:text-4xl"><GraduationCap className="h-7 w-7 text-wine-600" /> {formation.title}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-neutral-600">
                <span><Users className="mr-1 inline h-3.5 w-3.5" />{regs.length} inscrit{regs.length > 1 ? "s" : ""}</span>
                <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-bold text-emerald-700">{paidCount} payé{paidCount > 1 ? "s" : ""}</span>
                <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[10px] font-bold text-amber-800">{pendingCount} en attente</span>
              </div>
            </div>
            <button onClick={openNotify} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-wine-600" data-testid="formation-notify-contacts-btn">
              <Bell className="h-4 w-4" /> Notifier des contacts
            </button>
          </div>

          {sel.size > 0 && (
            <div className="mt-4">
              <BulkActionsBar count={sel.size} total={regs.length} onClear={clearSel} onSelectAll={selectAll}
                onDelete={() => setReminderOpen(true)} label="participant" testId="formation-bulk"
                extraActions={<button onClick={() => setReminderOpen(true)} className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-blue-700" data-testid="formation-reminder-btn"><Mail className="h-3.5 w-3.5" />Envoyer un rappel</button>}
              />
            </div>
          )}

          <div className="mt-6 overflow-x-auto rounded-2xl border border-neutral-100 bg-white">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-neutral-50 text-xs text-neutral-600">
                <tr>
                  <th className="px-3 py-2 w-8"><BulkCheckbox checked={regs.length > 0 && sel.size === regs.length} onChange={(v) => v ? selectAll() : clearSel()} testId="reg-select-all" /></th>
                  <th className="px-4 py-2 text-left">Participant</th>
                  <th className="px-4 py-2 text-left">Société</th>
                  <th className="px-4 py-2 text-right">Prix</th>
                  <th className="px-4 py-2 text-center">Statut</th>
                  <th className="px-4 py-2 text-center">Paiement</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {regs.map((r) => {
                  const paid = r.payment_status === "paid";
                  return (
                    <tr key={r.id} className={`border-t border-neutral-100 ${sel.has(r.id) ? "bg-rose-50/40" : ""}`} data-testid={`reg-row-${r.id}`}>
                      <td className="px-3 py-3"><BulkCheckbox checked={sel.has(r.id)} onChange={() => toggleSel(r.id)} testId={`reg-select-${r.id}`} /></td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-navy">{r.first_name} {r.last_name}</div>
                        <div className="text-xs text-neutral-500">{r.user_email}</div>
                      </td>
                      <td className="px-4 py-3 text-neutral-700">{r.company || "—"}</td>
                      <td className="px-4 py-3 text-right font-semibold">{Number(r.price || 0).toFixed(0)} {(r.currency || "EUR").toUpperCase()}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${r.status === "confirmed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>{r.status === "confirmed" ? "Confirmé" : "En attente"}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${paid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`} title={r.manual_payment ? "Marqué payé manuellement" : undefined} data-testid={`reg-pay-${r.id}`}>
                          {paid ? "✅ Payé" : "💳 En attente"}
                          {r.manual_payment && " (manuel)"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center justify-end gap-1">
                          {!paid && r.price > 0 && (
                            <button onClick={() => resendStripe(r.id)} className="inline-flex items-center gap-1 rounded-full border border-blue-300 bg-white px-2.5 py-0.5 text-[10px] font-bold text-blue-700 hover:bg-blue-50" title="Régénérer & envoyer le lien Stripe" data-testid={`reg-resend-stripe-${r.id}`}>
                              <RefreshCw className="h-3 w-3" /> Stripe
                            </button>
                          )}
                          {!paid && (
                            <button onClick={() => markPaid(r.id, true)} className="inline-flex items-center gap-1 rounded-full border border-emerald-500 bg-white px-2.5 py-0.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-50" data-testid={`reg-mark-paid-${r.id}`}>
                              💶 Marquer payé
                            </button>
                          )}
                          {paid && r.manual_payment && (
                            <button onClick={() => markPaid(r.id, false)} className="rounded-full bg-neutral-100 px-2.5 py-0.5 text-[10px] font-semibold text-neutral-700 hover:bg-neutral-200" data-testid={`reg-unmark-paid-${r.id}`}>Annuler</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {regs.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-neutral-500">Aucun inscrit pour le moment.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Reminder modal */}
      {reminderOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setReminderOpen(false)}>
          <form onSubmit={sendReminder} className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="reminder-modal">
            <h3 className="text-xl font-bold text-navy">Envoyer un rappel</h3>
            <p className="mt-1 text-xs text-neutral-500">{sel.size} participant{sel.size > 1 ? "s" : ""} sélectionné{sel.size > 1 ? "s" : ""}.</p>
            <input value={reminderSubject} onChange={(e) => setReminderSubject(e.target.value)} placeholder="Sujet (par défaut : Rappel — votre formation à venir)" className="mt-4 w-full rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="reminder-subject" />
            <textarea rows={6} value={reminderBody} onChange={(e) => setReminderBody(e.target.value)} placeholder="Message HTML (laisser vide pour un message par défaut)" className="mt-3 w-full rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="reminder-body" />
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setReminderOpen(false)} className="rounded-full border border-neutral-300 px-4 py-2 text-sm">Annuler</button>
              <button type="submit" disabled={sending} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="reminder-send">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Notify contacts modal */}
      {notifyOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setNotifyOpen(false)}>
          <form onSubmit={sendNotify} className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="notify-modal">
            <h3 className="flex items-center gap-2 text-xl font-bold text-navy"><Bell className="h-5 w-5 text-wine-500" />Notifier des contacts</h3>
            <p className="mt-1 text-xs text-neutral-500">Envoyer la fiche formation à des contacts triés ou à des emails libres.</p>

            <div className="mt-4 relative">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-3.5 w-3.5 text-neutral-400" />
              <input value={contactQuery} onChange={(e) => setContactQuery(e.target.value)} placeholder="Rechercher un contact (nom, email, société…)" className="w-full rounded-xl border border-neutral-200 pl-8 pr-3 py-2 text-sm" data-testid="notify-search" />
            </div>

            <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-neutral-100" data-testid="notify-contacts-list">
              {contactsLoading ? <div className="p-3 text-center"><Loader2 className="inline h-4 w-4 animate-spin text-wine-500" /></div>
                : filteredContacts.length === 0 ? <div className="p-3 text-center text-xs text-neutral-500">Aucun contact</div>
                : filteredContacts.slice(0, 60).map((c) => (
                  <label key={c.id} className="flex items-center gap-2 border-b border-neutral-100 px-3 py-1.5 text-xs hover:bg-wine-50/30 cursor-pointer">
                    <input type="checkbox" checked={selContacts.has(c.id)} onChange={() => toggleContact(c.id)} className="h-3.5 w-3.5" data-testid={`notify-contact-${c.id}`} />
                    <span className="flex-1 truncate"><strong>{c.first_name || ""} {c.last_name || ""}</strong> <span className="text-neutral-500">— {c.email}</span> {c.company_name && <em className="text-neutral-400">@ {c.company_name}</em>}</span>
                  </label>
                ))}
            </div>
            <div className="mt-1 text-[11px] text-neutral-500">{selContacts.size} contact{selContacts.size > 1 ? "s" : ""} sélectionné{selContacts.size > 1 ? "s" : ""}.</div>

            <label className="mt-4 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">+ Emails libres (séparés par , ou ;)</label>
            <input value={extraEmails} onChange={(e) => setExtraEmails(e.target.value)} placeholder="alice@example.com, bob@example.com" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="notify-extra-emails" />

            <label className="mt-4 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Message d&apos;intro (optionnel)</label>
            <textarea rows={3} value={notifyMsg} onChange={(e) => setNotifyMsg(e.target.value)} placeholder="Texte d'introduction personnalisé…" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="notify-message" />

            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setNotifyOpen(false)} className="rounded-full border border-neutral-300 px-4 py-2 text-sm">Annuler</button>
              <button type="submit" disabled={sending} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="notify-send">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer la notification
              </button>
            </div>
          </form>
        </div>
      )}
    </PortalLayout>
  );
}
